/**
 * MobileDrawer — Left slide-in navigation drawer (mobile + tablet)
 *
 * Wraps SidebarInner in a fixed overlay panel that slides in from the left.
 * Used on all viewports below lg (< 1024px).
 *
 * Behaviour:
 *   - Slide in/out with CSS transform transition (no JS animation library)
 *   - Semi-transparent backdrop closes the drawer on click
 *   - Escape key closes the drawer
 *   - Body scroll is locked while open (prevents background scroll on iOS)
 *   - Drawer is always expanded (collapsed=false); no collapse button shown
 *   - Route changes are handled in Layout.jsx (useEffect on pathname)
 *
 * Props:
 *   open               boolean  — whether the drawer is visible
 *   onClose            fn       — called to close the drawer
 *   onOpenCmdPalette   fn       — called when the ⌘K button inside is tapped
 */

import React, { useEffect } from "react";
import SidebarInner from "@/components/SidebarInner";
import useOverlayScrollLock from "@/hooks/useOverlayScrollLock";

export default function MobileDrawer({ open, onClose, onOpenCmdPalette }) {
  useOverlayScrollLock(open);
  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  return (
    <>
      {/* ── Backdrop ────────────────────────────────────────────── */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0, 0, 0, 0.45)",
          // One below modal so the drawer panel sits on top
          zIndex: "calc(var(--flg-z-modal) - 1)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 260ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />

      {/* ── Drawer panel ────────────────────────────────────────── */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        data-testid="mobile-drawer"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "var(--flg-sidebar-w)",   /* 264px — same as desktop */
          height: "100dvh",
          background: "var(--flg-bg-elevated)",
          borderRight: "1px solid var(--flg-border)",
          display: "flex",
          flexDirection: "column",
          zIndex: "var(--flg-z-modal)",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 280ms cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: open ? "var(--flg-shadow-xl)" : "none",
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <SidebarInner
          collapsed={false}
          setCollapsed={null}
          showCollapseButton={false}
          onOpenCmdPalette={onOpenCmdPalette}
        />
      </div>
    </>
  );
}
